import { useEffect } from 'react';
import { useDocumentTitle } from './useDocumentTitle';
import { GameState, GameResult } from '../types/game';
import { StockData } from '../types/game';
import { StockCategoryFilter, CategoryDisplayConfig } from '../types/stockCategories';
import { createLogger } from '../utils/logger';

interface UseGameEffectsProps {
  category?: StockCategoryFilter;
  categoryConfig?: CategoryDisplayConfig;
  currentCategory: StockCategoryFilter;
  gameState: GameState;
  apiStockData?: StockData | null;
  isStockLoading?: boolean;
  isSubmitting?: boolean;
  updateGameState: (updates: Partial<GameState>) => void;
  setGameResult: (result: GameResult | null) => void;
  generateGameResult: () => GameResult | null;
}

const gameEffectsLogger = createLogger('GameEffects', { flag: 'GAME_EFFECTS' });

/**
 * Custom hook for managing game-related side effects and synchronization
 * Extracted from GamePage to improve maintainability
 */
export const useGameEffects = ({
  category,
  categoryConfig,
  currentCategory,
  gameState,
  apiStockData,
  isStockLoading,
  isSubmitting,
  updateGameState,
  setGameResult,
  generateGameResult
}: UseGameEffectsProps) => {

  // Set document title (only if no category is provided, since CategoryGamePage handles it)
  useDocumentTitle(
    categoryConfig
      ? `Wallstreetle | ${categoryConfig.label} Stock Game`
      : 'Wallstreetle | Daily Stock Market Game'
  );

  // PHASE 4: Effect Chain Analysis - Stock Data Sync
  useEffect(() => {
    const effectId = 'StockDataSync';
    const startTime = performance.now();
    const loggerEnabled = gameEffectsLogger.isEnabled();
    const summary: Record<string, unknown> | null = loggerEnabled
      ? {
          effectId,
          triggers: {
            apiSymbol: apiStockData?.Symbol,
            gameSymbol: gameState.solution?.Symbol,
            category: category || currentCategory
          }
        }
      : null;

    // Skip during category switch — route category differs from context category.
    // switchCategory handles state transition; acting here would corrupt the old category's state.
    const isSwitching = category !== undefined && category !== currentCategory;

    if (!isSwitching && apiStockData && (!gameState.solution || gameState.solution.Symbol !== apiStockData.Symbol)) {
      updateGameState({
        solution: apiStockData,
        isLoading: false,
        error: null
      });

      if (summary) {
        summary.action = 'sync';
        summary.symbols = {
          fetched: apiStockData.Symbol,
          previous: gameState.solution?.Symbol || null,
        };
      }
    } else if (summary) {
      const skipReasons: string[] = [];
      if (!apiStockData) skipReasons.push('NO_API_DATA');
      if (!gameState.solution) skipReasons.push('NO_SOLUTION');
      if (apiStockData?.Symbol === gameState.solution?.Symbol) skipReasons.push('SYMBOLS_MATCH');

      summary.action = 'skip';
      summary.details = {
        hasApiData: !!apiStockData,
        hasSolution: !!gameState.solution,
        symbolMatch: apiStockData?.Symbol === gameState.solution?.Symbol,
        skipReasons
      };
    }

    if (summary) {
      summary.durationMs = Number((performance.now() - startTime).toFixed(2));
      gameEffectsLogger.throttled(
        `${effectId}-summary`,
        250,
        'debug',
        summary
      );
    }
  }, [
    apiStockData,
    gameState.solution,
    category,
    currentCategory,
    updateGameState
  ]); // Keep object dependencies for correctness

  // PHASE 4: Enhanced category change detection and state reset
  useEffect(() => {
    const effectId = 'CategoryChange';
    const startTime = performance.now();
    const currentCat = category || currentCategory;
    const loggerEnabled = gameEffectsLogger.isEnabled();
    const summary: Record<string, unknown> | null = loggerEnabled
      ? {
          effectId,
          category: currentCat,
          url: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
          triggers: {
            hasCategoryParam: !!category,
            hasCurrentCategory: !!currentCategory,
            hasApiStockData: !!apiStockData,
            apiSymbol: apiStockData?.Symbol
          }
        }
      : null;

    // Only reset if we have a mismatch AND we're not already loading or submitting
    // Don't reset completed games — preserves guess history on category switch
    // Don't reset during category switch — switchCategory handles state transition
    const isCatSwitching = category !== undefined && category !== currentCategory;
    if (
      gameState.solution &&
      apiStockData &&
      gameState.solution.Symbol !== apiStockData.Symbol &&
      !gameState.isGameComplete &&
      !isCatSwitching &&
      !gameState.isLoading &&
      !isStockLoading &&
      !isSubmitting
    ) {
      if (summary) {
        summary.action = 'reset';
        summary.mismatch = {
          gameSymbol: gameState.solution.Symbol,
          apiSymbol: apiStockData.Symbol,
        };
        summary.snapshot = {
          guesses: gameState.guesses.length,
          currentRow: gameState.currentRow,
          status: gameState.gameStatus,
        };
      }

      updateGameState({
        solution: apiStockData,
        guesses: [],
        currentRow: 0,
        currentGuess: '',
        visibleHints: 1,
        attempts: 4,
        gameStatus: 'playing' as const,
        isGameComplete: false,
        isLoading: false,
        error: null
      });
    } else if (summary) {
      const skipReasons: string[] = [];
      if (!gameState.solution) skipReasons.push('NO_SOLUTION');
      if (!apiStockData) skipReasons.push('NO_API_DATA');
      if (gameState.solution?.Symbol === apiStockData?.Symbol) skipReasons.push('SYMBOLS_MATCH');
      if (gameState.isLoading) skipReasons.push('ALREADY_LOADING');
      if (isStockLoading) skipReasons.push('FETCH_IN_PROGRESS');
      if (isSubmitting) skipReasons.push('SUBMISSION_IN_PROGRESS');

      summary.action = 'skip';
      summary.details = {
        skipReasons,
        symbolComparison: {
          gameSymbol: gameState.solution?.Symbol,
          apiSymbol: apiStockData?.Symbol,
          match: gameState.solution?.Symbol === apiStockData?.Symbol
        }
      };
    }

    if (summary) {
      summary.durationMs = Number((performance.now() - startTime).toFixed(2));
      gameEffectsLogger.throttled(
        `${effectId}-summary`,
        250,
        'debug',
        summary
      );
    }
  }, [
    category,
    currentCategory,
    apiStockData,
    gameState.isLoading,
    gameState.isGameComplete,
    isStockLoading,
    isSubmitting,
    gameState.solution,
    gameState.guesses,
    gameState.currentRow,
    gameState.gameStatus,
    updateGameState
  ]); // Keep object dependencies for correctness

  // PHASE 4: Enhanced game completion events
  useEffect(() => {
    const effectId = 'GameCompletion';
    const startTime = performance.now();
    const loggerEnabled = gameEffectsLogger.isEnabled();
    const summary: Record<string, unknown> | null = loggerEnabled
      ? {
          effectId,
          gameStatus: gameState.gameStatus,
          guessCount: gameState.guesses.length,
          solution: gameState.solution?.Symbol
        }
      : null;

    if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') {
      try {
        const result = generateGameResult();
        if (result) {
          setGameResult(result);
          if (summary) {
            summary.action = 'result-generated';
            summary.result = {
              solution: result.solution,
              isWin: result.isWin,
              attempts: result.attempts
            };
          }
        } else {
          setGameResult(null);
          if (summary) {
            summary.action = 'result-missing';
          }
        }
      } catch (error) {
        if (summary) {
          summary.action = 'result-error';
          summary.error = error instanceof Error ? error.message : 'Unknown error';
        }
      }
    } else {
      setGameResult(null);
      if (summary) {
        summary.action = 'in-progress';
        summary.isPlaying = gameState.gameStatus === 'playing';
      }
    }

    if (summary) {
      summary.durationMs = Number((performance.now() - startTime).toFixed(2));
      gameEffectsLogger.throttled(
        `${effectId}-summary`,
        250,
        'debug',
        summary
      );
    }
  }, [
    gameState.gameStatus,
    gameState.guesses,
    gameState.solution,
    generateGameResult,
    setGameResult
  ]); // Keep object dependencies for correctness

  // PHASE 4: Enhanced debug logging to track the fix effectiveness
  useEffect(() => {
    if (!gameEffectsLogger.isEnabled()) {
      return;
    }

    const effectId = 'StateSync';
    const timestamp = Date.now();
    const currentCat = category || currentCategory;
    const triggers: string[] = [];
    if (category) triggers.push('category-param');
    if (currentCategory) triggers.push('current-category');
    if (apiStockData?.Symbol) triggers.push('api-symbol');
    if (gameState.solution?.Symbol) triggers.push('game-symbol');
    if (gameState.isLoading !== undefined) triggers.push('loading-state');

    const performanceNow = typeof performance !== 'undefined' ? performance.now() : 0;
    const memoryUsed =
      typeof performance !== 'undefined' &&
      (performance as any).memory &&
      (performance as any).memory.usedJSHeapSize
        ? `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB`
        : 'N/A';

    gameEffectsLogger.throttled(
      `${effectId}-summary`,
      300,
      'debug',
      {
        effectId,
        timestamp,
        category: currentCat,
        url: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
        state: {
          gameStatus: gameState.gameStatus,
          isLoading: gameState.isLoading,
          currentRow: gameState.currentRow,
          guessCount: gameState.guesses.length,
          hasApiData: !!apiStockData,
          hasSolution: !!gameState.solution,
          symbolMatch: apiStockData?.Symbol === gameState.solution?.Symbol,
          error: gameState.error ? 'present' : null
        },
        triggers,
        performance: {
          timestamp: performanceNow,
          memoryUsed
        }
      }
    );
  }, [
    category,
    currentCategory,
    apiStockData,
    gameState.solution,
    gameState.isLoading,
    gameState.gameStatus,
    gameState.currentRow,
    gameState.guesses,
    gameState.error
  ]);

  // PHASE 4: Master Effect Lifecycle Monitoring & Error Boundary Debug Integration
  useEffect(() => {
    if (!gameEffectsLogger.isEnabled()) {
      return;
    }

    const effectId = 'LifecycleMonitor';
    const sessionId = `session_${Date.now()}`;

    gameEffectsLogger.info('Lifecycle entry', {
      effectId,
      sessionId,
      timestamp: new Date().toISOString(),
      category: category || currentCategory,
      url: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
      userAgent: typeof navigator !== 'undefined' ? `${navigator.userAgent.substring(0, 80)}...` : 'N/A'
    });

    // Effect execution counter for loop detection
    let executionCount = 0;

    const incrementCounter = () => {
      executionCount++;
      if (executionCount > 10) {
        gameEffectsLogger.error('Potential infinite loop detected', { effectId, executionCount });
      } else if (executionCount > 5) {
        gameEffectsLogger.warn('High execution count detected', { effectId, executionCount });
      }
    };

    incrementCounter();

    // Global error monitoring
    const errorHandler = (event: ErrorEvent) => {
      gameEffectsLogger.error('Global error detected', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        category: category || currentCategory,
        gameState: {
          status: gameState.gameStatus,
          loading: gameState.isLoading,
          hasError: !!gameState.error
        }
      });
    };

    const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      gameEffectsLogger.error('Unhandled promise rejection', {
        reason: event.reason instanceof Error ? event.reason.message : event.reason,
        category: category || currentCategory,
        gameStatus: gameState.gameStatus
      });
    };

    // Add error listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('error', errorHandler);
      window.addEventListener('unhandledrejection', unhandledRejectionHandler);
    }

    // Performance regression detection
    const performanceNow = typeof performance !== 'undefined' ? performance.now() : 0;
    const memoryUsage =
      typeof performance !== 'undefined' &&
      (performance as any).memory &&
      (performance as any).memory.usedJSHeapSize
        ? `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB`
        : 'N/A';

    if (performanceNow > 0) {
      gameEffectsLogger.throttled(
        `${effectId}-performance`,
        500,
        'debug',
        {
          effectId,
          timestamp: performanceNow,
          memoryUsage,
          executionCount
        }
      );
    }

    // Cleanup function
    return () => {
      gameEffectsLogger.info('Lifecycle exit', {
        effectId,
        sessionId,
        finalExecutionCount: executionCount,
        category: category || currentCategory,
        cleanupTimestamp: new Date().toISOString()
      });

      // Remove error listeners
      if (typeof window !== 'undefined') {
        window.removeEventListener('error', errorHandler);
        window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
      }
    };
  }); // Intentionally no dependencies - runs on every render to detect loops

  if (gameEffectsLogger.isEnabled()) {
    gameEffectsLogger.throttled(
      'render-complete',
      250,
      'debug',
      {
        timestamp: Date.now(),
        category: category || currentCategory,
        renderCount: 'tracked_per_effect'
      }
    );
  }

  return {
    // This hook primarily manages side effects, no return values needed
    // All effects are handled internally
  };
};
